/**
 * 应用更新服务
 *
 * 桌面端（macOS/Windows/Linux）：
 *   - 通过 tauri-plugin-updater 检查更新（已嵌入签名验证）
 *   - 支持两种模式：
 *     1. 传统模式：downloadAndInstallUpdate() 一次性下载安装
 *     2. 现代模式：startBackgroundDownload() + installDownloadedUpdate()
 *        实现"后台下载 + 用户确认安装"的用户体验
 *   - 通过 tauri-plugin-process 重启应用
 *
 * 移动端（Android）：
 *   - 通过 HTTP API 检查更新（/api/releases/latest）
 *   - 弹出 Modal 显示 changelog 和下载链接
 *   - 通过 tauri-plugin-shell 打开系统浏览器下载 APK
 *
 * 非桌面端不调用 tauri-plugin-updater（plugin 未注册），
 * 通过 isTauriMobilePlatform() 守卫切换实现路径。
 */
import { isTauriMobilePlatform } from '../utils/mobile'

// 应用版本号（由 Vite 注入，需要在 vite.config 中定义 define: { __APP_VERSION__: ... }）
// 兜底：从 package.json 读取或硬编码
declare const __APP_VERSION__: string
const APP_VERSION: string =
  (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) || '1.0.0'

// Admin-TSC 后端 API 基础 URL（支持多个回退）
// 环境变量优先，其次主域名，最后回退域名
const RELEASES_API_BASES = [
  (import.meta as any).env?.VITE_RELEASES_API_BASE,
  'https://share.lingshinote.cn/api',
  'https://admin.lingshinote.cn/api',
].filter(Boolean) as string[]

// 调试日志
console.log('[UpdateService] APP_VERSION:', APP_VERSION)
console.log('[UpdateService] API_BASES:', RELEASES_API_BASES)

export interface UpdateInfo {
  hasUpdate: boolean
  version: string
  notes: string
  pubDate?: string
  downloadUrl?: string
  signature?: string
  forceUpdate?: boolean
  minRequiredVersion?: string | null
  dismissed?: boolean
}

export interface DownloadProgress {
  downloaded: number
  total: number
}

/** 获取当前应用版本号 */
export function getAppVersion(): string {
  return APP_VERSION
}

/**
 * 检测是否在 Tauri 桌面端环境中运行
 */
function isTauriDesktop(): boolean {
  if (isTauriMobilePlatform()) return false
  return '__TAURI__' in window || '__TAURI_INTERNALS__' in window
}

/**
 * 检查更新（统一入口）
 *
 * 桌面端：调用 tauri-plugin-updater 的 check()，自动遵循 tauri.conf.json 中配置的 endpoints
 * 移动端：直接调用 /api/releases/latest 接口
 *
 * 返回 null 表示无需更新或检查失败（静默处理）
 */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  try {
    if (isTauriMobilePlatform()) {
      console.log('[UpdateService] 使用移动端检查更新')
      return await checkForUpdateMobile()
    }

    if (!isTauriDesktop()) {
      console.log('[UpdateService] 非 Tauri 环境，跳过更新检查')
      return null
    }

    console.log('[UpdateService] 使用桌面端检查更新')
    return await checkForUpdateDesktop()
  } catch (err) {
    console.warn('[UpdateService] 检查更新失败（静默处理）:', err)
    return null
  }
}

/**
 * 桌面端：通过 tauri-plugin-updater 检查更新
 */
async function checkForUpdateDesktop(): Promise<UpdateInfo | null> {
  try {
    const { check } = await import('@tauri-apps/plugin-updater')
    console.log('[UpdateService] 调用 updater.check()...')
    const update = await check()
    console.log('[UpdateService] updater.check() 返回:', update ? { version: update.version, available: update.available } : null)

    if (!update || !update.available) {
      return {
        hasUpdate: false,
        version: APP_VERSION,
        notes: '',
      }
    }

    return {
      hasUpdate: true,
      version: update.version,
      notes: update.body || '',
      pubDate: update.date ? new Date(update.date).toISOString() : undefined,
      // 桌面端下载和签名由 plugin 内部管理，不需要暴露给前端
      downloadUrl: undefined,
      signature: undefined,
      forceUpdate: false, // 桌面端不强制，由用户决定是否更新
    }
  } catch (err: any) {
    console.error('[UpdateService] 桌面端检查更新异常:', err)
    throw new Error(`检查更新失败: ${err?.message || '未知错误'}`)
  }
}

/**
 * 移动端：通过 HTTP API 检查更新（支持多个回退 URL）
 */
async function checkForUpdateMobile(): Promise<UpdateInfo | null> {
  const currentVersion = APP_VERSION
  console.log('[UpdateService] 移动端检查更新, 当前版本:', currentVersion)

  // 尝试多个回退 URL
  let lastError: Error | null = null
  for (const baseUrl of RELEASES_API_BASES) {
    try {
      const url = `${baseUrl}/releases/latest?platform=android&current_version=${encodeURIComponent(currentVersion)}`
      console.log('[UpdateService] 请求:', url)

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      })

      if (!response.ok) {
        console.warn(`[UpdateService] 请求失败 [${response.status}]:`, url)
        lastError = new Error(`HTTP ${response.status}`)
        continue
      }

      const data = await response.json()
      console.log('[UpdateService] 响应数据:', data)

      return {
        hasUpdate: Boolean(data.hasUpdate),
        version: data.latestVersion || APP_VERSION,
        notes: data.changelog || '',
        pubDate: data.pubDate,
        downloadUrl: data.downloadUrl,
        forceUpdate: Boolean(data.forceUpdate),
        minRequiredVersion: data.minRequiredVersion || null,
      }
    } catch (err: any) {
      console.warn('[UpdateService] 请求异常:', err?.message)
      lastError = err
      continue
    }
  }

  // 所有 URL 都失败
  console.error('[UpdateService] 所有 URL 都请求失败')
  throw lastError || new Error('无法连接到更新服务器')
}

/**
 * 桌面端：下载并安装更新（传统模式，一次性完成）
 *
 * @param onProgress 进度回调（可选）
 */
export async function downloadAndInstallUpdate(
  onProgress?: (progress: DownloadProgress) => void
): Promise<void> {
  if (isTauriMobilePlatform()) {
    throw new Error('桌面端专用 API，移动端请使用 downloadAndroidApk')
  }

  if (!isTauriDesktop()) {
    throw new Error('非 Tauri 环境不支持更新')
  }

  try {
    const { check } = await import('@tauri-apps/plugin-updater')
    const { relaunch } = await import('@tauri-apps/plugin-process')

    console.log('[UpdateService] 传统模式：检查更新...')
    const update = await check()
    if (!update || !update.available) {
      throw new Error('没有可用更新')
    }

    console.log('[UpdateService] 传统模式：开始下载并安装 v' + update.version)

    let downloaded = 0
    let contentLength = 0

    await update.downloadAndInstall((event: any) => {
      switch (event.event) {
        case 'Started':
          contentLength = event.data.contentLength || 0
          console.log('[UpdateService] 下载开始，总大小:', contentLength, 'bytes')
          if (onProgress) {
            onProgress({ downloaded: 0, total: contentLength })
          }
          break
        case 'Progress':
          downloaded += event.data.chunkLength
          if (onProgress && contentLength > 0) {
            onProgress({ downloaded, total: contentLength })
          }
          break
        case 'Finished':
          console.log('[UpdateService] 下载完成')
          if (onProgress) {
            onProgress({ downloaded: contentLength, total: contentLength })
          }
          break
      }
    })

    console.log('[UpdateService] 安装完成，即将重启...')
    // 下载安装完成后重启应用
    await relaunch()
  } catch (err: any) {
    console.error('[UpdateService] 传统模式更新失败:', err)
    throw new Error(`更新失败: ${err?.message || '未知错误'}`)
  }
}

// 模块级变量：存储已下载的 Update 对象，供 installDownloadedUpdate 复用
let downloadedUpdate: any = null

/**
 * 桌面端：后台下载更新（仅下载，不安装）
 *
 * 用于"后台下载 + 用户确认安装"的现代更新流程：
 * 1. 检测到新版本后自动调用此方法开始后台下载
 * 2. 下载完成后显示"立即更新"按钮
 * 3. 用户点击后调用 installDownloadedUpdate() 安装
 *
 * @param onProgress 进度回调（可选）
 */
export async function startBackgroundDownload(
  onProgress?: (progress: DownloadProgress) => void
): Promise<void> {
  if (isTauriMobilePlatform()) {
    throw new Error('桌面端专用 API，移动端请使用 downloadAndroidApk')
  }

  if (!isTauriDesktop()) {
    throw new Error('非 Tauri 环境不支持更新')
  }

  try {
    const { check } = await import('@tauri-apps/plugin-updater')

    console.log('[UpdateService] 后台下载模式：检查更新...')
    const update = await check()
    if (!update || !update.available) {
      throw new Error('没有可用更新')
    }

    console.log('[UpdateService] 后台下载模式：开始下载 v' + update.version)

    let downloaded = 0
    let contentLength = 0

    // 使用 download() 而非 downloadAndInstall()，仅下载不安装
    // 回调内部加 try-catch，防止 Tauri 异步事件格式不匹配导致 uncaught error
    await update.download((event: any) => {
      try {
        switch (event.event) {
          case 'Started':
            contentLength = event.data?.contentLength || 0
            console.log('[UpdateService] 后台下载开始，总大小:', contentLength, 'bytes')
            if (onProgress) {
              onProgress({ downloaded: 0, total: contentLength })
            }
            break
          case 'Progress':
            downloaded += event.data?.chunkLength || 0
            if (onProgress && contentLength > 0) {
              onProgress({ downloaded, total: contentLength })
            }
            break
          case 'Finished':
            console.log('[UpdateService] 后台下载完成')
            if (onProgress) {
              onProgress({ downloaded: contentLength, total: contentLength })
            }
            break
        }
      } catch (callbackErr) {
        console.error('[UpdateService] 下载回调错误:', callbackErr)
      }
    })

    console.log('[UpdateService] 后台下载成功完成')
    downloadedUpdate = update
  } catch (err: any) {
    console.error('[UpdateService] 后台下载失败:', err)
    throw new Error(`后台下载失败: ${err?.message || '未知错误'}`)
  }
}

/**
 * 桌面端：安装已下载的更新
 *
 * 与 startBackgroundDownload 配合使用，用于"后台下载 + 用户确认安装"流程。
 * 调用后会安装之前下载的更新包并重启应用。
 */
export async function installDownloadedUpdate(): Promise<void> {
  if (isTauriMobilePlatform()) {
    throw new Error('桌面端专用 API，移动端请使用 downloadAndroidApk')
  }

  if (!isTauriDesktop()) {
    throw new Error('非 Tauri 环境不支持更新')
  }

  try {
    const { check } = await import('@tauri-apps/plugin-updater')
    const { relaunch } = await import('@tauri-apps/plugin-process')

    // 优先复用 startBackgroundDownload 存储的 Update 对象
    let update = downloadedUpdate
    if (!update) {
      // 兜底：如果没有存储的 Update 对象（如页面刷新后），重新 check
      console.log('[UpdateService] 无缓存的 Update 对象，重新检查...')
      update = await check()
    }
    if (!update || !update.available) {
      throw new Error('没有待安装的更新')
    }

    console.log('[UpdateService] 开始安装已下载的更新...')

    // 使用 install() 而非 downloadAndInstall()，直接安装已下载的包
    await update.install()

    console.log('[UpdateService] 安装完成，即将重启...')
    // 安装完成后重启应用
    await relaunch()
  } catch (err: any) {
    console.error('[UpdateService] 安装失败:', err)
    throw new Error(`安装失败: ${err?.message || '未知错误'}`)
  }
}

/**
 * 移动端已下载的 APK 本地路径（模块级缓存）
 */
let downloadedApkPath: string | null = null

/**
 * 移动端：通过 shell 打开浏览器下载 APK（传统回退方式）
 *
 * 调用 tauri-plugin-shell 的 open 函数，使用系统默认浏览器打开下载链接
 */
export async function downloadAndroidApk(url: string): Promise<void> {
  if (!isTauriMobilePlatform()) {
    throw new Error('移动端专用 API，桌面端请使用 downloadAndInstallUpdate')
  }

  // URL 安全校验：必须是 HTTPS 且在白名单域名
  if (!isSafeDownloadUrl(url)) {
    throw new Error('下载链接不安全，必须是 HTTPS 且在白名单域名内')
  }

  console.log('[UpdateService] 打开浏览器下载 APK:', url)

  const { open } = await import('@tauri-apps/plugin-shell')
  await open(url)
}

/**
 * 移动端：在应用内后台下载 APK（现代模式）
 *
 * 使用 tauri-plugin-upload 的 download 函数将 APK 下载到应用缓存目录，
 * 下载过程中可以回调进度，下载完成后通过 installDownloadedApk 安装。
 *
 * @param url APK 下载地址（HTTPS）
 * @param onProgress 下载进度回调（可选）
 * @returns 下载完成后的本地 APK 文件路径
 */
export async function startAndroidBackgroundDownload(
  url: string,
  onProgress?: (progress: DownloadProgress) => void
): Promise<string> {
  if (!isTauriMobilePlatform()) {
    throw new Error('移动端专用 API，桌面端请使用 startBackgroundDownload')
  }

  // URL 安全校验
  if (!isSafeDownloadUrl(url)) {
    throw new Error('下载链接不安全，必须是 HTTPS 且在白名单域名内')
  }

  console.log('[UpdateService] 开始后台下载 APK:', url)

  try {
    // 1. 获取应用缓存目录并拼接 APK 保存路径
    const { appCacheDir, join } = await import('@tauri-apps/api/path')
    const cacheDir = await appCacheDir()
    // 文件名：update-{timestamp}.apk，避免旧缓存冲突
    const timestamp = Date.now()
    const apkFileName = `update-${timestamp}.apk`
    const savePath = await join(cacheDir, apkFileName)

    console.log('[UpdateService] APK 保存路径:', savePath)

    // 2. 使用 upload 插件的 download 函数下载
    const { download } = await import('@tauri-apps/plugin-upload')

    await download(
      url,
      savePath,
      // 进度回调：upload 插件回调参数是 { progress, total } 或 (progress, total)
      // 根据官方文档 v2：回调签名为 ({ progress, total }) => void
      (payload: any) => {
        try {
          // 兼容两种签名：对象形式或两个数字参数
          let downloaded: number
          let total: number
          if (typeof payload === 'object' && payload !== null) {
            downloaded = Number(payload.progress || payload.progressTotal || 0)
            total = Number(payload.total || 0)
          } else {
            // 兜底：如果位置参数形式
            downloaded = Number(arguments[0] || 0)
            total = Number(arguments[1] || 0)
          }

          if (onProgress && total > 0) {
            onProgress({ downloaded, total })
          }
        } catch (e) {
          console.warn('[UpdateService] 下载进度回调处理异常:', e)
        }
      },
      // 可选 headers：添加 User-Agent 避免被某些 CDN 拦截（类型要求 Map<string, string>）
      new Map<string, string>([
        ['User-Agent', 'LingshiNote-Android-Updater/1.0'],
        ['Accept', 'application/vnd.android.package-archive, */*'],
      ])
    )

    console.log('[UpdateService] APK 后台下载完成:', savePath)
    downloadedApkPath = savePath
    return savePath
  } catch (err: any) {
    console.error('[UpdateService] APK 后台下载失败:', err)
    downloadedApkPath = null
    throw new Error(`APK 下载失败: ${err?.message || '未知错误'}`)
  }
}

/**
 * 移动端：安装已下载的 APK
 *
 * 使用 tauri-plugin-android-installer 启动系统安装器界面。
 * Android 限制：无法静默安装，必须弹出系统确认对话框。
 *
 * @param apkPath 本地 APK 文件路径（可选，默认使用上次下载的路径）
 */
export async function installDownloadedApk(apkPath?: string): Promise<void> {
  if (!isTauriMobilePlatform()) {
    throw new Error('移动端专用 API，桌面端请使用 installDownloadedUpdate')
  }

  const targetPath = apkPath || downloadedApkPath
  if (!targetPath) {
    throw new Error('没有找到已下载的 APK 文件，请先下载')
  }

  console.log('[UpdateService] 准备安装 APK:', targetPath)

  try {
    // 动态导入 android-installer 插件的 JS API
    const installerApi = await import('tauri-plugin-android-installer-api')

    // 1. 先检查是否可以安装（是否有权限）
    const canInstall = await installerApi.canInstall()
    console.log('[UpdateService] 安装权限检查: canInstall =', canInstall)

    if (!canInstall) {
      // 2. 请求安装未知应用权限
      console.log('[UpdateService] 无安装权限，正在请求用户授权...')
      await installerApi.requestInstallPermission()
      // 用户授权返回后再次检查（部分机型授权返回后立即能装，部分需要返回 app 后才生效）
      const canInstallAfter = await installerApi.canInstall()
      if (!canInstallAfter) {
        throw new Error('未获得 APK 安装权限，请在系统设置中允许此应用安装未知应用')
      }
    }

    // 3. 调用安装（会弹出系统安装器 UI）
    console.log('[UpdateService] 启动系统 APK 安装器...')
    await installerApi.install(targetPath)

    // 4. 安装器成功启动（用户点确认后 Android 会自动替换本 app 并杀掉进程，这里不会收到安装成功回调）
    console.log('[UpdateService] 系统安装器已启动，等待用户确认安装...')
  } catch (err: any) {
    console.error('[UpdateService] APK 安装启动失败:', err)
    const msg = err?.message || String(err || '未知错误')
    // 友好提示：如果插件加载失败则退回浏览器打开下载页的方式
    if (msg.includes('Cannot find module') || msg.includes('not supported on Android')) {
      throw new Error(`APK 安装启动失败: ${msg}，请尝试手动下载安装`)
    }
    throw new Error(`APK 安装启动失败: ${msg}`)
  }
}

/**
 * 校验下载 URL 是否安全
 */
function isSafeDownloadUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    // APK 通过 CNAME 域名 HTTP 下载（七牛禁止默认 endpoint 分发 APK，CNAME 无 HTTPS 证书）
    // APK 是公开安装包，签名参数泄露风险低
    const APK_CNAME_HOST = 'apk.lingshinote.cn'
    if (parsed.hostname === APK_CNAME_HOST && parsed.protocol === 'http:') return true
    // 其他所有 URL 必须 HTTPS
    if (parsed.protocol !== 'https:') return false
    // 下载源：七牛对象存储（自定义 CNAME 主域 lingshinote.cn + 官方默认域名形态）
    // 与 GitHub 发布渠道
    const allowedHosts = [
      'lingshinote.cn',    // 自定义 CNAME（七牛/CDN）
      'qiniucdn.com',      // 七牛官方默认域名
      'qbox.link',         // 七牛共享域名
      'github.com',
      'githubusercontent.com',
    ]
    return allowedHosts.some(
      (host) => parsed.hostname === host || parsed.hostname.endsWith('.' + host)
    )
  } catch {
    return false
  }
}

/**
 * 提供给前端的统一更新执行入口（传统模式：下载 + 安装）
 * - 桌面端：调用 downloadAndInstallUpdate
 * - 移动端：调用 downloadAndroidApk（浏览器打开下载，传统回退方式）
 */
export async function performUpdate(
  onProgress?: (progress: DownloadProgress) => void,
  mobileDownloadUrl?: string
): Promise<void> {
  if (isTauriMobilePlatform()) {
    if (!mobileDownloadUrl) {
      throw new Error('移动端更新需要提供下载 URL')
    }
    // 移动端传统模式：优先浏览器打开下载（保留向后兼容）
    await downloadAndroidApk(mobileDownloadUrl)
  } else {
    await downloadAndInstallUpdate(onProgress)
  }
}

/**
 * 提供给前端的后台下载入口（现代模式：仅下载，不安装）
 * - 桌面端：调用 startBackgroundDownload（tauri-plugin-updater）
 * - 移动端：调用 startAndroidBackgroundDownload（plugin-upload 下载到缓存）
 */
export async function performBackgroundDownload(
  onProgress?: (progress: DownloadProgress) => void,
  mobileDownloadUrl?: string
): Promise<void> {
  if (isTauriMobilePlatform()) {
    if (!mobileDownloadUrl) {
      throw new Error('移动端后台下载需要提供下载 URL')
    }
    await startAndroidBackgroundDownload(mobileDownloadUrl, onProgress)
    return
  }
  if (!isTauriDesktop()) {
    throw new Error('非 Tauri 桌面端环境不支持后台下载')
  }
  await startBackgroundDownload(onProgress)
}

/**
 * 提供给前端的安装入口（现代模式：安装已下载的更新）
 * - 桌面端：调用 installDownloadedUpdate（tauri-plugin-updater 安装 + 重启）
 * - 移动端：调用 installDownloadedApk（android-installer 启动系统安装器）
 */
export async function performInstallDownloaded(): Promise<void> {
  if (isTauriMobilePlatform()) {
    await installDownloadedApk()
    return
  }
  if (!isTauriDesktop()) {
    throw new Error('非 Tauri 桌面端环境不支持安装操作')
  }
  await installDownloadedUpdate()
}
